import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Popover } from "@remo-co/ui-core/src/components/Popover";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { IEventDirectoryFirestoreDoc } from "modules/eventDirectory/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectCurrentEventId,
  selectIsEventManager,
} from "modules/event/selectors";
import { IEvent } from "modules/event/types";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { EVENT_DIRECTORY_REFRESH_INTERVAL } from "modules/eventDirectory/constants";
import { selectTheaterSpaces } from "modules/theater/selectors";
import classNames from "classnames";
import { selectUserEmail, selectUserId } from "modules/auth/redux/selectors";
import { selectEventMembers } from "modules/eventMember/redux/selectors";
import { isEventManager as checkIsEventManger } from "modules/event/utils/eventUtils";
import { selectIsInBroadcast } from "modules/broadcast/redux/selectors";
import { useStyles } from "./styles";
import EventCard from "../EventCard";

interface Props {
  eventDirectories: IEventDirectoryFirestoreDoc[];
}

const EventDirectorySwitch = ({ eventDirectories }: Props) => {
  const isInBroadcast = useSelector(selectIsInBroadcast);
  const styles = useStyles({ isInBroadcast });
  const [showMenu, setShowMenu] = useState(false);
  const { t } = useI18n(["event"]);
  const eventMapRef = useRef(null);
  const isEventManager = useSelector(selectIsEventManager);
  const currentUserId = useSelector(selectUserId);
  const currentEventId = useSelector(selectCurrentEventId);
  const { checkIfEventHappeningNow, checkIfEventIsUpcoming } =
    useCheckEventStatus();
  const spaces = useSelector(selectTheaterSpaces);
  const userEmail = useSelector(selectUserEmail);
  const invitedEventMembers = useSelector(selectEventMembers);

  const isOngoingEvent = useCallback(
    (event: IEvent) =>
      event.id !== currentEventId &&
      (checkIfEventHappeningNow(event) ||
        (isEventManager && checkIfEventIsUpcoming(event))),
    // we dont want to re-render because of the non-memoized event check fns
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEventManager],
  );

  const isUserAllowedToEnter = useCallback(
    (event: IEvent) => {
      const emailDomain = userEmail?.split("@")[1];
      const userWithDomain = event?.domain === emailDomain;
      return (
        checkIsEventManger(event, currentUserId as string) ||
        (invitedEventMembers[event.id] &&
          invitedEventMembers[event.id].includes(userEmail as string)) ||
        !event?.isPrivate ||
        userWithDomain
      );
    },
    [currentUserId, invitedEventMembers, userEmail],
  );

  const getUniqueEvents = useCallback(
    (events: Partial<IEvent>[]) => {
      const uniqueEventsIds: Record<string, boolean> = {};
      return events.filter((event) => {
        const id = event?.id;

        if (id && !uniqueEventsIds[id]) {
          uniqueEventsIds[id] = true;
          return (
            isOngoingEvent(event as IEvent) &&
            isUserAllowedToEnter(event as IEvent)
          );
        }

        return false;
      });
    },
    [isOngoingEvent, isUserAllowedToEnter],
  );

  const uniqueEvents = (eventDirectories ?? []).reduce(
    (acc: Partial<IEvent>[], directory) => {
      if (directory !== null && directory.events) {
        acc.push(
          ...directory.events.map((event) => ({
            ...event,
            managers: directory.managers ?? [],
          })),
        );
      }
      return acc;
    },
    [],
  );

  const [availableEvents, setAvailableEvents] = useState(
    getUniqueEvents(uniqueEvents),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const events = (eventDirectories ?? []).reduce(
        (acc: Partial<IEvent>[], directory) => {
          if (directory !== null && directory.events) {
            acc.push(
              ...directory.events.map((event) => ({
                ...event,
                managers: directory.managers ?? [],
              })),
            );
          }
          return acc;
        },
        [],
      );
      setAvailableEvents(getUniqueEvents(events));
    }, EVENT_DIRECTORY_REFRESH_INTERVAL);

    return () => {
      window.clearInterval(timer);
    };
  }, [eventDirectories, getUniqueEvents]);

  const toggleMenu = () => {
    setShowMenu((isVisible) => !isVisible);
  };

  if (availableEvents.length < 1) {
    return null;
  }

  return (
    <>
      <Button
        ref={eventMapRef}
        onClick={toggleMenu}
        variant="text"
        className={classNames(styles.eventMap, {
          [styles.rightBorder]: spaces.length >= 1,
        })}
        color="dark"
      >
        <Typography variant="h4">{t("event:top.bar.event.map")}</Typography>
      </Button>
      <Popover
        anchorEl={eventMapRef.current}
        anchorOrigin={{
          horizontal: "center",
          vertical: "bottom",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        open={showMenu}
        onClose={toggleMenu}
        classes={{ paper: styles.menuPopover }}
      >
        <Container
          flex
          justifyContent="space-between"
          className={styles.headerContainer}
        >
          <Typography variant="h3">
            {t("event:top.bar.other.related.events")}
          </Typography>
          <Close onClick={toggleMenu} className={styles.closeButton} />
        </Container>
        <div className={styles.cardsContainer}>
          {availableEvents.map((event) => (
            <EventCard event={event} currentEventId={currentEventId || ""} />
          ))}
        </div>
      </Popover>
    </>
  );
};

export default EventDirectorySwitch;
